/**
 * Curated role hub config for /jobs/role/{slug}, /tyopaikat/ammatti/{slug},
 * /lediga-jobb/yrke/{slug} pages.
 *
 * History: this started (Aug 2026) as two healthcare roles on the theory that demand collapses
 * after them and "thin role pages do more harm than good". Both pages were thin because they
 * filtered the sector-level `field` facet with role names (0 and 1 matching jobs), not because
 * of demand: by `normalisedTitle` the same roles had 141 and 270 live jobs, and the demand figures
 * cited at the time (myyjä 1.0K/mo, sähköasentaja 800/mo) are solid. Role hubs now filter
 * `normalisedTitle` and the list is grown data-driven: any Finnish role with 20+ live jobs by
 * normalisedTitle qualifies (well above the SRP indexability floor of 5, so no page is ever
 * thin; a role whose inventory dips simply goes noindex for that day via the normal robots rules).
 *
 * `titleValues` were read from live Typesense normalisedTitle facet counts per collection
 * (2026-09-04) and include the spelling/case variants that actually occur. Canonical value first;
 * roles are ordered by live fi job count (desc) so callers can take a prefix as "top roles".
 * Curation rules used when the list was generated (keep them when adding roles):
 * - fi is primary. A role's fi values are the exact facet strings (case-sensitive in Typesense),
 *   so casing variants and "X sijaisuus"/"X, kotihoito" forms are listed explicitly.
 * - Near-duplicate fi titles are merged into one slug (Timpuri into Kirvesmies, Tuotejohtaja into
 *   Tuotepäällikkö, Ohjelmistosuunnittelija/-insinööri into Ohjelmistokehittäjä, ...).
 * - en/sv values are the titles the SAME jobs carry in the en/sv collections (often the untranslated
 *   Finnish title). Each en/sv value belongs to at most one role (the one where it is most frequent)
 *   so two role pages never share the same English/Swedish inventory, and values that would pull in
 *   unrelated jobs across the whole index ("Technician", "Instructor") are left out even when they
 *   occur. A role whose en/sv values are thin just goes noindex in that locale via the normal rules.
 * - Not roles, so excluded: Avoin hakemus, Generalisti, Johtaja, Harjoittelija, Hoitaja.
 */

import type { RoleDefinition, SupportedLocale } from '../../types/role-hub';
import { LOCALIZED_PATHS } from '../constants/url-constants';

/** Path markers for the role segment per locale (en, fi, sv). */
export const ROLE_PATH_MARKERS = ['role', 'ammatti', 'yrke'] as const;

export function isRolePathMarker(segment: string): boolean {
  return (ROLE_PATH_MARKERS as readonly string[]).includes(segment.toLowerCase());
}

export const ROLE_HUB_CONFIG: RoleDefinition[] = [
  {
    slug: 'henkilokohtainen-avustaja',
    titleValues: {
      fi: ['Henkilökohtainen avustaja', 'Aarre Avustajat - Henkilökohtainen avustaja', 'Aarre Avustajat - Henkilökohtainen avustaja - Pohjois-Pohjanmaa', 'Aarre Avustajat - Henkilökohtainen avustaja - Keski', 'Aarre Avustajat - Henkilökohtainen avustaja - Pirkanmaa', 'Aarre Avustajat - Henkilökohtainen avustaja - Pohjanmaa', 'Aarre Avustajat - Henkilökohtainen avustaja - Kanta-Häme'],
      en: ['Personal Assistant', 'Personal assistant', 'Personal care assistant'],
      sv: ['Personlig assistent', 'Personlig Assistent', 'Personal Assistant'],
    },
    displayNames: { fi: 'Henkilökohtainen avustaja', en: 'Personal Assistant', sv: 'Personlig assistent' },
    slugs: { en: 'personal-assistant', sv: 'personlig-assistent' },
  },
  {
    slug: 'lahihoitaja',
    titleValues: {
      fi: ['Lähihoitaja', 'Lähihoitajan sijaisuus', 'Lähihoitajan keikkatyö', 'LÄHIHOITAJA', 'Lähihoitaja, kotihoito', 'Lähihoitaja (75 %)'],
      en: ['Practical Nurse', 'Healthcare Assistant', 'Practical nurse', 'Healthcare assistant', 'Nurse Assistant', 'Care Assistant', 'Lähihoitaja', 'Home Care Assistant', 'Licensed Practical Nurse'],
      sv: ['Undersköterska', 'Lähihoitaja', 'Närvårdare', 'Praktisk sjuksköterska', 'Vårdassistent', 'Praktisk sjukskötare', 'Praktisk undersköterska', 'Vårdbiträde', 'Lähihoitajan keikkatyö', 'Närvårdarvikariat', 'Vikariat som närvårdare', 'Närvårdare vikariat', 'Närvårdare, hemvård'],
    },
    displayNames: { fi: 'Lähihoitaja', en: 'Practical Nurse', sv: 'Närvårdare' },
    slugs: { en: 'practical-nurse', sv: 'narvardare' },
  },
  {
    slug: 'myyja',
    titleValues: {
      fi: ['Myyjä'],
      en: ['Sales Assistant', 'Salesperson', 'Sales Associate'],
      sv: ['Säljare', 'Myyjä', 'Försäljningsassistent', 'Säljassistent', 'Butikssäljare', 'Butiksförsäljare', 'Butiksbiträde'],
    },
    displayNames: { fi: 'Myyjä', en: 'Salesperson', sv: 'Säljare' },
    slugs: { en: 'salesperson', sv: 'saljare' },
  },
  {
    slug: 'kokki',
    titleValues: {
      fi: ['Kokki', 'kokki'],
      en: ['Cook', 'Chef', 'Kokki', 'chef'],
      sv: ['Kock', 'Kokki', 'Kocka'],
    },
    displayNames: { fi: 'Kokki', en: 'Cook', sv: 'Kock' },
    slugs: { en: 'cook', sv: 'kock' },
  },
  {
    slug: 'sahkoasentaja',
    titleValues: {
      fi: ['Sähköasentaja'],
      en: ['Electrician', 'Electrical Installer', 'Electrical Technician', 'Electrical Assembler'],
      sv: ['Elektriker', 'Elinstallatör', 'Installationselektriker', 'Elektrisk installatör', 'Installations elektriker', 'Elmontör', 'Electrician', 'Elektrisk tekniker'],
    },
    displayNames: { fi: 'Sähköasentaja', en: 'Electrician', sv: 'Elektriker' },
    slugs: { en: 'electrician', sv: 'elektriker' },
  },
  {
    slug: 'ohjelmistokehittaja',
    titleValues: {
      fi: ['Ohjelmistokehittäjä', 'Ohjelmistosuunnittelija', 'Ohjelmistoinsinööri'],
      en: ['Software Engineer', 'Software Developer'],
      sv: ['Mjukvaruingenjör', 'Programvaruingenjör', 'Programvaruutvecklare', 'Softwareingenjör', 'Mjukvaruutvecklare', 'Programvarutekniker', 'Mjukvarutekniker', 'Systemutvecklare', 'Software Engineer', 'Programmerare', 'Softwareutvecklare', 'Ohjelmistokehittäjä', 'Programvarudesigner'],
    },
    displayNames: { fi: 'Ohjelmistokehittäjä', en: 'Software Engineer', sv: 'Mjukvaruutvecklare' },
    slugs: { en: 'software-engineer', sv: 'mjukvaruutvecklare' },
  },
  {
    slug: 'rakennustyontekija',
    titleValues: {
      fi: ['Rakennustyöntekijä'],
      en: ['Construction Worker'],
      sv: ['Rakennustyöntekijä', 'Byggarbetare', 'Byggnadsarbetare', 'Construction Worker'],
    },
    displayNames: { fi: 'Rakennustyöntekijä', en: 'Construction Worker', sv: 'Byggarbetare' },
    slugs: { en: 'construction-worker', sv: 'byggarbetare' },
  },
  {
    slug: 'kaivinkoneenkuljettaja',
    titleValues: {
      fi: ['Kaivinkoneenkuljettaja'],
      en: ['Excavator Operator', 'Kaivinkoneenkuljettaja', 'Excavator Driver', 'kaivinkoneenkuljettaja', 'Earthmoving Equipment Operator'],
      sv: ['Kaivinkoneenkuljettaja', 'Grävmaskinförare', 'Grävmaskinsförare', 'Grävmaskinoperatör', 'Grävmaskinist', 'kaivinkoneenkuljettaja', 'Grävmaskinsoperatör', 'Excavator Operator'],
    },
    displayNames: { fi: 'Kaivinkoneenkuljettaja', en: 'Excavator Operator', sv: 'Grävmaskinförare' },
    slugs: { en: 'excavator-operator', sv: 'gravmaskinforare' },
  },
  {
    slug: 'account-executive',
    titleValues: {
      fi: ['Account Executive', 'Enterprise Account Executive'],
      en: ['Account Executive', 'Enterprise Account Executive'],
      sv: ['Account Executive', 'Enterprise Account Executive'],
    },
    displayNames: { fi: 'Account Executive', en: 'Account Executive', sv: 'Account Executive' },
  },
  {
    slug: 'cnc-koneistaja',
    titleValues: {
      fi: ['CNC-koneistaja', 'CNC-Koneistaja'],
      en: ['CNC Machinist', 'CNC-koneistaja', 'CNC Machine Operator', 'CNC-Koneistaja', 'CNC -Koneistaja', 'CNC machine operator', 'CNC- koneistaja', 'CNC koneistaja', 'CNC Koneistaja'],
      sv: ['CNC-koneistaja', 'CNC-maskinist', 'CNC-maskinoperatör', 'CNC-fräsare', 'CNC-Koneistaja', 'CNC Machinist', 'CNC-operatör', 'CNC-verkstadsmaskinoperatör', 'CNC-fräsmaskinoperatör', 'CNC -Koneistaja', 'CNC Maskinoperatör'],
    },
    displayNames: { fi: 'CNC-koneistaja', en: 'CNC Machinist', sv: 'CNC-maskinist' },
    slugs: { en: 'cnc-machinist', sv: 'cnc-maskinist' },
  },
  {
    slug: 'hitsaaja',
    titleValues: {
      fi: ['Hitsaaja'],
      en: ['Welder', 'Hitsaaja'],
      sv: ['Svetsare', 'Hitsaaja'],
    },
    displayNames: { fi: 'Hitsaaja', en: 'Welder', sv: 'Svetsare' },
    slugs: { en: 'welder', sv: 'svetsare' },
  },
  {
    slug: 'projektipaallikko',
    titleValues: {
      fi: ['Projektipäällikkö'],
      en: ['Project Manager', 'Project Lead'],
      sv: ['Projektledare', 'Projektipäällikkö'],
    },
    displayNames: { fi: 'Projektipäällikkö', en: 'Project Manager', sv: 'Projektledare' },
    slugs: { en: 'project-manager', sv: 'projektledare' },
  },
  {
    slug: 'tarjoilija',
    titleValues: {
      fi: ['Tarjoilija'],
      en: ['Waiter', 'Tarjoilija', 'Waitress', 'Waiter/Waitress', 'Waitstaff'],
      sv: ['Tarjoilija', 'Servitör'],
    },
    displayNames: { fi: 'Tarjoilija', en: 'Waiter', sv: 'Servitör' },
    slugs: { en: 'waiter', sv: 'servitor' },
  },
  {
    slug: 'siivooja',
    titleValues: {
      fi: ['Siivooja'],
      en: ['Cleaner', 'Housekeeper', 'Building Cleaner', 'Siivooja', 'Room Attendant'],
      sv: ['Städare', 'Fastighetsstädare', 'Städerska', 'Hushållerska', 'Siivooja', 'Hemhjälp'],
    },
    displayNames: { fi: 'Siivooja', en: 'Cleaner', sv: 'Städare' },
    slugs: { en: 'cleaner', sv: 'stadare' },
  },
  {
    slug: 'sairaanhoitaja',
    titleValues: {
      fi: ['Sairaanhoitaja', 'SAIRAANHOITAJA', 'Sairaanhoitajan sijaisuus'],
      en: ['Nurse', 'Registered Nurse', 'Home Care Nurse', 'Specialist Nurse'],
      sv: ['Sjuksköterska', 'Sjukskötare', 'Nurse', 'Timvikarierande sjuksköterska'],
    },
    // Finland-Swedish title (Valvira's "sjukskötare"; see lambda/utils/finland-swedish-role-terms.ts).
    displayNames: { fi: 'Sairaanhoitaja', en: 'Registered Nurse', sv: 'Sjukskötare' },
    slugs: { en: 'registered-nurse', sv: 'sjukskotare' },
    retiredSlugs: ['sjukskoterska'],
  },
  {
    slug: 'tuotepaallikko',
    titleValues: {
      fi: ['Tuotepäällikkö', 'Tuotejohtaja'],
      en: ['Product Manager', 'Product Director', 'Head of Product', 'Product Lead', 'Product Management Director'],
      sv: ['Produktchef', 'Produktdirektör', 'Produktledare', 'Produktledningsdirektör'],
    },
    displayNames: { fi: 'Tuotepäällikkö', en: 'Product Manager', sv: 'Produktchef' },
    slugs: { en: 'product-manager', sv: 'produktchef' },
  },
  {
    slug: 'kirvesmies',
    titleValues: {
      fi: ['Kirvesmies', 'Timpuri'],
      en: ['Carpenter', 'Kirvesmies', 'Timpuri'],
      sv: ['Snickare', 'Kirvesmies', 'Timpuri', 'Timmerman', 'Carpenter'],
    },
    displayNames: { fi: 'Kirvesmies', en: 'Carpenter', sv: 'Snickare' },
    slugs: { en: 'carpenter', sv: 'snickare' },
  },
  {
    slug: 'maanrakennustyontekija',
    titleValues: {
      fi: ['Maanrakennustyöntekijä'],
      en: ['Earthworks Worker', 'Earthmoving Worker', 'Road Construction Worker', 'Civil Engineering Worker', 'Road construction worker', 'Civil engineering worker', 'Groundworks Worker', 'Civil engineering labourer', 'Civil Engineering Labourer', 'Groundworker'],
      sv: ['Maanrakennustyöntekijä', 'Anläggningsarbetare', 'Markarbetare', 'Jord- och anläggningsarbetare', 'Jordarbetare', 'Mark- och anläggningsarbetare', 'Earthworks Worker', 'Mark- och schaktarbetare'],
    },
    displayNames: { fi: 'Maanrakennustyöntekijä', en: 'Earthworks Worker', sv: 'Anläggningsarbetare' },
    slugs: { en: 'earthworks-worker', sv: 'anlaggningsarbetare' },
  },
  {
    slug: 'asiakaspalvelija',
    titleValues: {
      fi: ['Asiakaspalvelija', 'Asiakaspalvelun edustaja', 'Asiakaspalvelu', 'Asiakaspalvelun asiantuntija'],
      en: ['Customer Service Representative', 'Customer Service Agent', 'Customer Service', 'Customer Advisor', 'Customer Support Agent', 'Customer Care', 'Customer Service Specialist', 'Guest Services Associate', 'Customer Care Representative', 'Customer Support Specialist'],
      sv: ['Kundtjänstrepresentant', 'Kundtjänstmedarbetare', 'Kundtjänstagent', 'Kundtjänst', 'Kundrådgivare', 'Kundtjänstspecialist', 'Kundservice', 'Kundsupport', 'Kundsupportagent', 'Gästserviceassistent', 'Kundsupportmedarbetare'],
    },
    displayNames: { fi: 'Asiakaspalvelija', en: 'Customer Service Representative', sv: 'Kundtjänstmedarbetare' },
    slugs: { en: 'customer-service-representative', sv: 'kundtjanstmedarbetare' },
  },
  {
    slug: 'myyntiedustaja',
    titleValues: {
      fi: ['Myyntiedustaja'],
      en: ['Sales Representative', 'Myyntiedustaja'],
      sv: ['Säljrepresentant', 'Försäljningsrepresentant', 'Myyntiedustaja', 'Säljsrepresentant', 'Sales Representative'],
    },
    displayNames: { fi: 'Myyntiedustaja', en: 'Sales Representative', sv: 'Säljrepresentant' },
    slugs: { en: 'sales-representative', sv: 'saljrepresentant' },
  },
  {
    slug: 'parturi-kampaaja',
    titleValues: {
      fi: ['Parturi-Kampaaja', 'Parturi-kampaaja', 'parturi-kampaaja', 'Parturi'],
      en: ['Parturi-Kampaaja', 'Parturi-kampaaja', 'Parturi', 'Hairdresser', 'Barber', 'parturi-kampaaja', 'parturi', 'Parturi- Kampaaja'],
      sv: ['Parturi-Kampaaja', 'Parturi-kampaaja', 'Parturi', 'Frisör', 'parturi-kampaaja', 'Barberare', 'parturi', 'Parturi- Kampaaja'],
    },
    displayNames: { fi: 'Parturi-kampaaja', en: 'Hairdresser', sv: 'Frisör' },
    slugs: { en: 'hairdresser', sv: 'frisor' },
  },
  {
    slug: 'englannin-opettaja',
    titleValues: {
      fi: ['Englannin opettaja'],
      en: ['English Instructor', 'English Teacher'],
      sv: ['Engelska lärare', 'Engelska instruktör', 'Engelska Lärare', 'Engelsk instruktör', 'Engelsk lärare', 'Engelska Instruktör', 'Engelslärare'],
    },
    displayNames: { fi: 'Englannin opettaja', en: 'English Teacher', sv: 'Engelsklärare' },
    slugs: { en: 'english-teacher', sv: 'engelsklarare' },
  },
  {
    slug: 'myyntipaallikko',
    titleValues: {
      fi: ['Myyntipäällikkö'],
      en: ['Sales Manager'],
      sv: ['Säljchef', 'Försäljningschef', 'Myyntipäällikkö'],
    },
    displayNames: { fi: 'Myyntipäällikkö', en: 'Sales Manager', sv: 'Försäljningschef' },
    slugs: { en: 'sales-manager', sv: 'forsaljningschef' },
  },
  {
    slug: 'rakennusapulainen',
    titleValues: {
      fi: ['Rakennusapulainen', 'Rakennusaputyöntekijä'],
      en: ['Construction Assistant', 'Construction Helper', 'Rakennusapulainen', 'Building Construction Worker', 'Construction Labourer', 'Construction Laborer'],
      sv: ['Byggassistent', 'Rakennusapulainen', 'Rakennusaputyöntekijä', 'Byggnadsassistent'],
    },
    displayNames: { fi: 'Rakennusapulainen', en: 'Construction Helper', sv: 'Byggassistent' },
    slugs: { en: 'construction-helper', sv: 'byggassistent' },
  },
  {
    slug: 'tuotantotyontekija',
    titleValues: {
      fi: ['Tuotantotyöntekijä'],
      en: ['Production Worker'],
      sv: ['Produktionsarbetare', 'Tuotantotyöntekijä', 'Produktionarbetare'],
    },
    displayNames: { fi: 'Tuotantotyöntekijä', en: 'Production Worker', sv: 'Produktionsarbetare' },
    slugs: { en: 'production-worker', sv: 'produktionsarbetare' },
  },
  {
    slug: 'asiakkuuspaallikko',
    titleValues: {
      fi: ['Asiakkuuspäällikkö', 'Account Manager'],
      en: ['Account Manager', 'Customer Manager'],
      sv: ['Account Manager', 'Kundansvarig', 'Asiakkuuspäällikkö'],
    },
    displayNames: { fi: 'Asiakkuuspäällikkö', en: 'Account Manager', sv: 'Kundansvarig' },
    slugs: { en: 'account-manager', sv: 'kundansvarig' },
  },
  {
    slug: 'lvi-asentaja',
    titleValues: {
      fi: ['LVI-asentaja'],
      en: ['HVAC Installer', 'LVI Installer', 'HVAC Technician'],
      sv: ['VVS-installatör', 'VVS-montör', 'LVI-installatör', 'Rörläggare', 'VVS-tekniker', 'HVAC Installer'],
    },
    displayNames: { fi: 'LVI-asentaja', en: 'HVAC Installer', sv: 'VVS-installatör' },
    slugs: { en: 'hvac-installer', sv: 'vvs-installator' },
  },
  {
    slug: 'yleislaakari',
    titleValues: {
      fi: ['Yleislääkäri'],
      en: ['General Practitioner'],
      sv: ['Allmänläkare'],
    },
    displayNames: { fi: 'Yleislääkäri', en: 'General Practitioner', sv: 'Allmänläkare' },
    slugs: { en: 'general-practitioner', sv: 'allmanlakare' },
  },
  {
    slug: 'varastotyontekija',
    titleValues: {
      fi: ['Varastotyöntekijä'],
      en: ['Warehouse Worker', 'Warehouse Operator', 'Stock Operator', 'Warehouse Technician'],
      sv: ['Lagerarbetare', 'Varastotyöntekijä', 'Lageroperatör', 'Lagertekniker'],
    },
    displayNames: { fi: 'Varastotyöntekijä', en: 'Warehouse Worker', sv: 'Lagerarbetare' },
    slugs: { en: 'warehouse-worker', sv: 'lagerarbetare' },
  },
  {
    slug: 'data-engineer',
    titleValues: {
      fi: ['Data Engineer', 'Data-insinööri'],
      en: ['Data Engineer'],
      sv: ['Data Engineer', 'Dataingenjör', 'Datatekniker'],
    },
    displayNames: { fi: 'Data Engineer', en: 'Data Engineer', sv: 'Data Engineer' },
  },
  {
    slug: 'kuorma-autonkuljettaja',
    titleValues: {
      fi: ['Kuorma-autonkuljettaja'],
      en: ['Truck Driver', 'Kuorma-autonkuljettaja', 'Truck driver'],
      sv: ['Kuorma-autonkuljettaja', 'Lastbilschaufför', 'Lastbilsförare'],
    },
    displayNames: { fi: 'Kuorma-autonkuljettaja', en: 'Truck Driver', sv: 'Lastbilschaufför' },
    slugs: { en: 'truck-driver', sv: 'lastbilschauffor' },
  },
  {
    slug: 'varhaiskasvatuksen-opettaja',
    titleValues: {
      fi: ['Varhaiskasvatuksen opettaja'],
      en: ['Early Childhood Education Teacher', 'Early Years Teacher', 'Early years teacher', 'Early Childhood Teacher', 'Early childhood teacher', 'Early childhood education teacher'],
      sv: ['Förskollärare', 'Lärare inom småbarnspedagogik', 'Förskolelärare'],
    },
    // Finland's title under lag om småbarnspedagogik 540/2018 (Finland has no "förskollärare").
    displayNames: { fi: 'Varhaiskasvatuksen opettaja', en: 'Early Childhood Teacher', sv: 'Lärare inom småbarnspedagogik' },
    slugs: { en: 'early-childhood-teacher', sv: 'larare-inom-smabarnspedagogik' },
    retiredSlugs: ['forskollarare'],
  },
  {
    slug: 'huoltoteknikko',
    titleValues: {
      fi: ['Huoltoteknikko', 'Huoltoteknikko I'],
      en: ['Maintenance Technician', 'Maintenance Technician I'],
      sv: ['Underhållstekniker', 'Underhållstekniker I', 'Service tekniker'],
    },
    displayNames: { fi: 'Huoltoteknikko', en: 'Maintenance Technician', sv: 'Underhållstekniker' },
    slugs: { en: 'maintenance-technician', sv: 'underhallstekniker' },
  },
  {
    slug: 'liiketoiminnan-kehitysjohtaja',
    titleValues: {
      fi: ['Liiketoiminnan kehitysjohtaja'],
      en: ['Business Development Manager', 'Director of Business Development', 'Business Development Lead', 'Head of Business Development', 'Business Development Director', 'Business Development Executive'],
      sv: ['Affärsutvecklingschef', 'Affärsutvecklare', 'Affärsutvecklingsledare', 'Affärsutvecklingsdirektör'],
    },
    displayNames: { fi: 'Liiketoiminnan kehitysjohtaja', en: 'Business Development Manager', sv: 'Affärsutvecklingschef' },
    slugs: { en: 'business-development-manager', sv: 'affarsutvecklingschef' },
  },
  {
    slug: 'laakari',
    titleValues: {
      fi: ['Lääkäri'],
      en: ['Doctor', 'Physician'],
      sv: ['Läkare', 'Lääkäri'],
    },
    displayNames: { fi: 'Lääkäri', en: 'Doctor', sv: 'Läkare' },
    slugs: { en: 'doctor', sv: 'lakare' },
  },
  {
    slug: 'putkiasentaja',
    titleValues: {
      fi: ['Putkiasentaja'],
      en: ['Plumber', 'Pipe Fitter', 'Pipefitter'],
      sv: ['Rörmontör', 'Rörmokare', 'Rörinstallatör'],
    },
    displayNames: { fi: 'Putkiasentaja', en: 'Plumber', sv: 'Rörmontör' },
    slugs: { en: 'plumber', sv: 'rormontor' },
  },
  {
    slug: 'purkutyontekija',
    titleValues: {
      fi: ['Purkutyöntekijä'],
      en: ['Demolition Worker', 'Dismantling Worker', 'Purkutyöntekijä'],
      sv: ['Purkutyöntekijä', 'Rivningsarbetare'],
    },
    displayNames: { fi: 'Purkutyöntekijä', en: 'Demolition Worker', sv: 'Rivningsarbetare' },
    slugs: { en: 'demolition-worker', sv: 'rivningsarbetare' },
  },
  {
    slug: 'ohjaaja',
    titleValues: {
      fi: ['Ohjaaja'],
      en: ['Counselor', 'Counsellor', 'Guidance Counselor', 'Support Worker'],
      sv: ['Ohjaaja', 'Vägledare', 'Handledare'],
    },
    displayNames: { fi: 'Ohjaaja', en: 'Counsellor', sv: 'Handledare' },
    slugs: { en: 'counsellor', sv: 'handledare' },
  },
  {
    slug: 'rengasasentaja',
    titleValues: {
      fi: ['Rengasasentaja'],
      en: ['Tire Fitter', 'Rengasasentaja', 'Tire Technician', 'Tire Installer', 'Tire fitter'],
      sv: ['Däckmontör', 'Rengasasentaja', 'Däckmonterare', 'Däcksmonterare'],
    },
    displayNames: { fi: 'Rengasasentaja', en: 'Tire Fitter', sv: 'Däckmontör' },
    slugs: { en: 'tire-fitter', sv: 'dackmontor' },
  },
  {
    slug: 'ravintolatyontekija',
    titleValues: {
      fi: ['Ravintolatyöntekijä'],
      en: ['Restaurant Worker', 'Restaurant worker'],
      sv: ['Ravintolatyöntekijä', 'Restaurangarbetare', 'Restaurangmedarbetare', 'Restaurangpersonal'],
    },
    displayNames: { fi: 'Ravintolatyöntekijä', en: 'Restaurant Worker', sv: 'Restaurangarbetare' },
    slugs: { en: 'restaurant-worker', sv: 'restaurangarbetare' },
  },
  {
    slug: 'hammaslaakari',
    titleValues: {
      fi: ['Hammaslääkäri'],
      en: ['Dentist'],
      sv: ['Tandläkare', 'Hammaslääkäri'],
    },
    displayNames: { fi: 'Hammaslääkäri', en: 'Dentist', sv: 'Tandläkare' },
    slugs: { en: 'dentist', sv: 'tandlakare' },
  },
  {
    slug: 'tyonjohtaja',
    titleValues: {
      fi: ['Työnjohtaja'],
      en: ['Site Supervisor', 'Foreman', 'Site Manager', 'Production Supervisor', 'Construction Supervisor', 'Workshop Supervisor', 'Construction Site Manager'],
      sv: ['Arbetsledare', 'Platschef', 'Byggledare', 'Byggarbetsledare', 'Produktionsledare', 'Työnjohtaja', 'Anläggningsledare', 'Verkstadschef'],
    },
    displayNames: { fi: 'Työnjohtaja', en: 'Site Supervisor', sv: 'Arbetsledare' },
    slugs: { en: 'site-supervisor', sv: 'arbetsledare' },
  },
  {
    slug: 'taksinkuljettaja',
    titleValues: {
      fi: ['Taksinkuljettaja'],
      en: ['Taksinkuljettaja', 'Taxi Driver', 'Taxi driver'],
      sv: ['Taksinkuljettaja', 'Taxiförare', 'Taxichaufför'],
    },
    displayNames: { fi: 'Taksinkuljettaja', en: 'Taxi Driver', sv: 'Taxiförare' },
    slugs: { en: 'taxi-driver', sv: 'taxiforare' },
  },
  {
    slug: 'sahkosuunnittelija',
    titleValues: {
      fi: ['Sähkösuunnittelija'],
      en: ['Electrical Designer', 'Electrical Design Engineer'],
      sv: ['Elkonstruktör', 'Elektroingenjör', 'Elektrisk konstruktör', 'Elprojektör', 'Elektrisk Designer', 'Elprojekterare', 'Elektrisk designer'],
    },
    displayNames: { fi: 'Sähkösuunnittelija', en: 'Electrical Designer', sv: 'Elkonstruktör' },
    slugs: { en: 'electrical-designer', sv: 'elkonstruktor' },
  },
  {
    slug: 'asentaja',
    titleValues: {
      fi: ['Asentaja'],
      en: ['Installer', 'Assembler', 'Industrial Machinery Mechanic', 'Industrial machinery mechanic'],
      sv: ['Installatör', 'Maskinmekaniker', 'Montör', 'Monterare'],
    },
    displayNames: { fi: 'Asentaja', en: 'Installer', sv: 'Montör' },
    slugs: { en: 'installer', sv: 'montor' },
  },
  {
    slug: 'kirjanpitaja',
    titleValues: {
      fi: ['Kirjanpitäjä'],
      en: ['Accountant', 'Bookkeeper'],
      sv: ['Kirjanpitäjä', 'Bokförare', 'Redovisningsekonom', 'Redovisningskonsult', 'Accountant'],
    },
    displayNames: { fi: 'Kirjanpitäjä', en: 'Accountant', sv: 'Bokförare' },
    slugs: { en: 'accountant', sv: 'bokforare' },
  },
  {
    slug: 'kiinteistonhoitaja',
    titleValues: {
      fi: ['Kiinteistönhoitaja'],
      en: ['Building caretaker', 'Property Caretaker', 'Property caretaker', 'Building Caretaker', 'Property Maintenance Worker', 'Property Maintenance Technician'],
      sv: ['Kiinteistönhoitaja', 'Fastighetsskötare', 'Fastighetsvårdare', 'Fastighetsvärd', 'Fastighetsvaktmästare'],
    },
    displayNames: { fi: 'Kiinteistönhoitaja', en: 'Property Caretaker', sv: 'Fastighetsskötare' },
    slugs: { en: 'property-caretaker', sv: 'fastighetsskotare' },
  },
  {
    slug: 'levyseppahitsaaja',
    titleValues: {
      fi: ['Levyseppähitsaaja'],
      en: ['Sheet Metal Welder', 'Plate Welder', 'Sheet metal welder'],
      sv: ['Levyseppähitsaaja', 'Plåtsvetsare', 'Plåtslagare', 'Plåtbearbetare', 'Plåtslagare svetsare'],
    },
    displayNames: { fi: 'Levyseppähitsaaja', en: 'Sheet Metal Welder', sv: 'Plåtsvetsare' },
    slugs: { en: 'sheet-metal-welder', sv: 'platsvetsare' },
  },
  {
    slug: 'myyntineuvottelija',
    titleValues: {
      fi: ['Myyntineuvottelija', 'Myyntineuvottelija - taloyhtiömyynti'],
      en: ['Sales Negotiator', 'Myyntineuvottelija'],
      sv: ['Säljförhandlare', 'Försäljningsförhandlare', 'Myyntineuvottelija'],
    },
    displayNames: { fi: 'Myyntineuvottelija', en: 'Sales Negotiator', sv: 'Säljförhandlare' },
    slugs: { en: 'sales-negotiator', sv: 'saljforhandlare' },
  },
  {
    slug: 'kotisiivooja',
    titleValues: {
      fi: ['Kotisiivooja'],
      en: ['Kotisiivooja', 'House Cleaner', 'House cleaner'],
      sv: ['Kotisiivooja', 'Hemstädare', 'Husstädare'],
    },
    displayNames: { fi: 'Kotisiivooja', en: 'House Cleaner', sv: 'Hemstädare' },
    slugs: { en: 'house-cleaner', sv: 'hemstadare' },
  },
  {
    slug: 'hammashoitaja',
    titleValues: {
      fi: ['Hammashoitaja'],
      en: ['Hammashoitaja', 'Dental Nurse', 'Dental Assistant', 'Dental Chairside Assistant', 'Dental nurse', 'Dental chairside assistant'],
      sv: ['Hammashoitaja', 'Tandhygienassistent', 'Tandskötare', 'Tandsköterska'],
    },
    displayNames: { fi: 'Hammashoitaja', en: 'Dental Nurse', sv: 'Tandskötare' },
    slugs: { en: 'dental-nurse', sv: 'tandskotare' },
  },
  {
    slug: 'koneistaja',
    titleValues: {
      fi: ['Koneistaja'],
      en: ['Machinist', 'Koneistaja', 'Machine Operator', 'Machining Technician'],
      sv: ['Maskinoperatör', 'Koneistaja', 'Maskinist'],
    },
    displayNames: { fi: 'Koneistaja', en: 'Machinist', sv: 'Maskinoperatör' },
    slugs: { en: 'machinist', sv: 'maskinoperator' },
  },
  {
    slug: 'tuotesuunnittelija',
    titleValues: {
      fi: ['Tuotesuunnittelija'],
      en: ['Product Designer', 'Product Design Engineer'],
      sv: ['Produktdesigner', 'Produktdesigningenjör'],
    },
    displayNames: { fi: 'Tuotesuunnittelija', en: 'Product Designer', sv: 'Produktdesigner' },
    slugs: { en: 'product-designer', sv: 'produktdesigner' },
  },
  {
    slug: 'sarmaaja',
    titleValues: {
      fi: ['Särmääjä'],
      en: ['Sheet Metal Worker', 'Sheet Metal Bender', 'Särmääjä', 'Shear Operator', 'Metal Products Assembler', 'Sheet Metal Shear Operator'],
      sv: ['Särmääjä', 'Plåtbockare', 'Montör, metallprodukter'],
    },
    displayNames: { fi: 'Särmääjä', en: 'Sheet Metal Bender', sv: 'Plåtbockare' },
    slugs: { en: 'sheet-metal-bender', sv: 'platbockare' },
  },
  {
    slug: 'rakennussiivooja',
    titleValues: {
      fi: ['Rakennussiivooja'],
      en: ['Construction Cleaner', 'Rakennussiivooja', 'Building Exterior Cleaner', 'Construction Site Cleaner', 'Building exterior cleaner'],
      sv: ['Byggstädare', 'Rakennussiivooja'],
    },
    displayNames: { fi: 'Rakennussiivooja', en: 'Construction Cleaner', sv: 'Byggstädare' },
    slugs: { en: 'construction-cleaner', sv: 'byggstadare' },
  },
  {
    slug: 'laatoittaja',
    titleValues: {
      fi: ['Laatoittaja'],
      en: ['Laatoittaja', 'Tile Setter', 'Tiler', 'Tile Installer'],
      sv: ['Laatoittaja', 'Kakeläggare', 'Kakelläggare', 'Plattsättare'],
    },
    displayNames: { fi: 'Laatoittaja', en: 'Tiler', sv: 'Kakelläggare' },
    slugs: { en: 'tiler', sv: 'kakellaggare' },
  },
  {
    slug: 'iv-asentaja',
    titleValues: {
      fi: ['IV-asentaja'],
      en: ['IV Installer', 'Ventilation Installer', 'IV-asentaja'],
      sv: ['IV-installatör', 'Ventilationsmontör', 'IV-asentaja', 'Ventilations- och kylmontör'],
    },
    displayNames: { fi: 'IV-asentaja', en: 'Ventilation Installer', sv: 'IV-installatör' },
    slugs: { en: 'ventilation-installer', sv: 'iv-installator' },
  },
  {
    slug: 'huoltoasentaja',
    titleValues: {
      fi: ['Huoltoasentaja'],
      en: ['Service Technician', 'Huoltoasentaja'],
      sv: ['Servicetekniker', 'Huoltoasentaja'],
    },
    displayNames: { fi: 'Huoltoasentaja', en: 'Service Technician', sv: 'Servicetekniker' },
    slugs: { en: 'service-technician', sv: 'servicetekniker' },
  },
  {
    slug: 'data-scientist',
    titleValues: {
      fi: ['Data Scientist'],
      en: ['Data Scientist'],
      sv: ['Data Scientist', 'Datascientist'],
    },
    displayNames: { fi: 'Data Scientist', en: 'Data Scientist', sv: 'Data Scientist' },
  },
  {
    slug: 'kunnossapitoasentaja',
    titleValues: {
      fi: ['Kunnossapitoasentaja'],
      en: ['Kunnossapitoasentaja', 'Maintenance Mechanic', 'Mechanical Maintenance Technician', 'Industrial Maintenance Technician'],
      sv: ['Kunnossapitoasentaja', 'Underhållsmontör', 'Underhållsmekaniker'],
    },
    displayNames: { fi: 'Kunnossapitoasentaja', en: 'Maintenance Mechanic', sv: 'Underhållsmontör' },
    slugs: { en: 'maintenance-mechanic', sv: 'underhallsmontor' },
  },
  {
    slug: 'varhaiskasvatuksen-lastenhoitaja',
    titleValues: {
      fi: ['Varhaiskasvatuksen lastenhoitaja'],
      en: ['Child day care worker', 'Early Childhood Educator', 'Varhaiskasvatuksen lastenhoitaja', 'Child Day Care Worker', 'Early childhood educator', 'Early childhood education assistant'],
      sv: ['Varhaiskasvatuksen lastenhoitaja', 'Förskolepedagog', 'varhaiskasvatuksen lastenhoitaja'],
    },
    displayNames: { fi: 'Varhaiskasvatuksen lastenhoitaja', en: 'Early Childhood Educator', sv: 'Barnskötare' },
    slugs: { en: 'early-childhood-educator', sv: 'barnskotare' },
  },
  {
    slug: 'sahkoinsinoori',
    titleValues: {
      fi: ['Sähköinsinööri'],
      en: ['Electrical Engineer'],
      sv: ['Elektrisk ingenjör', 'Elektrisk Ingenjör', 'Elingenjör', 'Elektronikingenjör'],
    },
    displayNames: { fi: 'Sähköinsinööri', en: 'Electrical Engineer', sv: 'Elingenjör' },
    slugs: { en: 'electrical-engineer', sv: 'elingenjor' },
  },
  {
    slug: 'metallityontekija',
    titleValues: {
      fi: ['Metallityöntekijä'],
      en: ['Metal Worker', 'Metalworker'],
      sv: ['Metallarbetare', 'Metallityöntekijä'],
    },
    displayNames: { fi: 'Metallityöntekijä', en: 'Metal Worker', sv: 'Metallarbetare' },
    slugs: { en: 'metal-worker', sv: 'metallarbetare' },
  },
  {
    slug: 'yhdistelmaajoneuvonkuljettaja',
    titleValues: {
      fi: ['Yhdistelmäajoneuvonkuljettaja'],
      en: ['Combination Vehicle Driver', 'Cargo Vehicle Driver', 'Combination vehicle driver', 'Articulated Vehicle Driver'],
      sv: ['Yhdistelmäajoneuvonkuljettaja', 'Kombinationsfordonförare', 'Kombinationsfordonsförare'],
    },
    displayNames: { fi: 'Yhdistelmäajoneuvonkuljettaja', en: 'Combination Vehicle Driver', sv: 'Kombinationsfordonsförare' },
    slugs: { en: 'combination-vehicle-driver', sv: 'kombinationsfordonsforare' },
  },
  {
    slug: 'raudoittaja',
    titleValues: {
      fi: ['Raudoittaja'],
      en: ['Raudoittaja', 'Rebar Worker', 'Steel Fixer', 'Rebar Ironworker'],
      sv: ['Armerare', 'Raudoittaja', 'Armeringsarbetare'],
    },
    displayNames: { fi: 'Raudoittaja', en: 'Rebar Worker', sv: 'Armerare' },
    slugs: { en: 'rebar-worker', sv: 'armerare' },
  },
  {
    slug: 'kaivinkoneen-peramies',
    titleValues: {
      fi: ['Kaivinkoneen perämies'],
      en: ['Excavator Foreman', 'Excavator Assistant'],
      sv: ['Kaivinkoneen perämies', 'Grävmaskinsförman', 'Grävmaskinsmedhjälpare'],
    },
    displayNames: { fi: 'Kaivinkoneen perämies', en: 'Excavator Foreman', sv: 'Grävmaskinsförman' },
    slugs: { en: 'excavator-foreman', sv: 'gravmaskinsforman' },
  },
  {
    slug: 'myyntijohtaja',
    titleValues: {
      fi: ['Myyntijohtaja'],
      en: ['Sales Director'],
      sv: ['Försäljningsdirektör', 'Säljdirektör'],
    },
    displayNames: { fi: 'Myyntijohtaja', en: 'Sales Director', sv: 'Försäljningsdirektör' },
    slugs: { en: 'sales-director', sv: 'forsaljningsdirektor' },
  },
  {
    slug: 'ajoneuvoasentaja',
    titleValues: {
      fi: ['Ajoneuvoasentaja'],
      en: ['Ajoneuvoasentaja', 'Vehicle Technician', 'ajoneuvoasentaja', 'Vehicle Mechanic', 'Automotive Technician'],
      sv: ['Ajoneuvoasentaja', 'Fordonstekniker', 'ajoneuvoasentaja'],
    },
    displayNames: { fi: 'Ajoneuvoasentaja', en: 'Vehicle Technician', sv: 'Fordonstekniker' },
    slugs: { en: 'vehicle-technician', sv: 'fordonstekniker' },
  },
  {
    slug: 'tyoterveyslaakari',
    titleValues: {
      fi: ['Työterveyslääkäri'],
      en: ['Occupational Health Physician', 'Occupational Health Doctor', 'Workplace Doctor'],
      sv: ['Arbetshälsoläkare', 'Arbetsmiljöläkare', 'Työterveyslääkäri', 'Arbetsplatsläkare', 'Företagshälsovårdsläkare'],
    },
    displayNames: { fi: 'Työterveyslääkäri', en: 'Occupational Health Physician', sv: 'Arbetshälsoläkare' },
    slugs: { en: 'occupational-health-physician', sv: 'arbetshalsolakare' },
  },
  {
    slug: 'teollisuusmaalari',
    titleValues: {
      fi: ['Teollisuusmaalari'],
      en: ['Teollisuusmaalari', 'Industrial Painter'],
      sv: ['Teollisuusmaalari', 'Industrimålare', 'Industrimalare', 'Industri-målare'],
    },
    displayNames: { fi: 'Teollisuusmaalari', en: 'Industrial Painter', sv: 'Industrimålare' },
    slugs: { en: 'industrial-painter', sv: 'industrimalare' },
  },
  {
    slug: 'cnc-sorvaaja',
    titleValues: {
      fi: ['CNC-sorvaaja', 'CNC-Sorvaaja'],
      en: ['CNC Lathe Operator', 'CNC-Sorvaaja', 'CNC Turner', 'CNC-sorvaaja', 'CNC Turning Operator'],
      sv: ['CNC-svarvare', 'CNC-sorvaaja', 'CNC-Sorvaaja'],
    },
    displayNames: { fi: 'CNC-sorvaaja', en: 'CNC Lathe Operator', sv: 'CNC-svarvare' },
    slugs: { en: 'cnc-lathe-operator', sv: 'cnc-svarvare' },
  },
  {
    slug: 'asiakashankkija',
    titleValues: {
      fi: ['Asiakashankkija'],
      en: ['Customer Acquisition', 'Customer Acquisition Specialist', 'Customer Acquisition Agent'],
      sv: ['Kundanskaffare', 'Asiakashankkija'],
    },
    displayNames: { fi: 'Asiakashankkija', en: 'Customer Acquisition Specialist', sv: 'Kundanskaffare' },
    slugs: { en: 'customer-acquisition-specialist', sv: 'kundanskaffare' },
  },
  {
    slug: 'ratkaisukonsultti',
    titleValues: {
      fi: ['Ratkaisukonsultti'],
      en: ['Solution Consultant', 'Solutions Consultant'],
      sv: ['Lösningskonsult', 'Solutionskonsult'],
    },
    displayNames: { fi: 'Ratkaisukonsultti', en: 'Solution Consultant', sv: 'Lösningskonsult' },
    slugs: { en: 'solution-consultant', sv: 'losningskonsult' },
  },
  {
    slug: 'automekaanikko',
    titleValues: {
      fi: ['Automekaanikko'],
      en: ['Automekaanikko', 'Automotive Mechanic', 'Car Mechanic', 'Car mechanic'],
      sv: ['Bilmekaniker', 'Automekaanikko'],
    },
    displayNames: { fi: 'Automekaanikko', en: 'Car Mechanic', sv: 'Bilmekaniker' },
    slugs: { en: 'car-mechanic', sv: 'bilmekaniker' },
  },
  {
    slug: 'maalari',
    titleValues: {
      fi: ['Maalari'],
      en: ['Painter', 'Maalari', 'Construction Painter'],
      sv: ['Målare', 'Maalari'],
    },
    displayNames: { fi: 'Maalari', en: 'Painter', sv: 'Målare' },
    slugs: { en: 'painter', sv: 'malare' },
  },
  {
    slug: 'hieroja',
    titleValues: {
      fi: ['Hieroja'],
      en: ['Massage Therapist', 'Hieroja'],
      sv: ['Massör', 'Hieroja'],
    },
    displayNames: { fi: 'Hieroja', en: 'Massage Therapist', sv: 'Massör' },
    slugs: { en: 'massage-therapist', sv: 'massor' },
  },
  {
    slug: 'ostaja',
    titleValues: {
      fi: ['Ostaja'],
      en: ['Buyer', 'Ostaja', 'Purchaser'],
      sv: ['Inköpare', 'Ostaja', 'Köpare'],
    },
    displayNames: { fi: 'Ostaja', en: 'Buyer', sv: 'Inköpare' },
    slugs: { en: 'buyer', sv: 'inkopare' },
  },
  {
    slug: 'analyytikko',
    titleValues: {
      fi: ['Analyytikko'],
      en: ['Analyst'],
      sv: ['Analytiker'],
    },
    displayNames: { fi: 'Analyytikko', en: 'Analyst', sv: 'Analytiker' },
    slugs: { en: 'analyst', sv: 'analytiker' },
  },
  {
    slug: 'kattoasentaja',
    titleValues: {
      fi: ['Kattoasentaja'],
      en: ['Roof Installer', 'Kattoasentaja', 'Roofer'],
      sv: ['Takmontör', 'Kattoasentaja', 'Takläggare'],
    },
    displayNames: { fi: 'Kattoasentaja', en: 'Roofer', sv: 'Takmontör' },
    slugs: { en: 'roofer', sv: 'takmontor' },
  },
  {
    slug: 'puheterapeutti',
    titleValues: {
      fi: ['Puheterapeutti'],
      en: ['Speech and Language Therapist', 'Speech Therapist', 'Puheterapeutti', 'Speech and language therapist'],
      sv: ['Puheterapeutti', 'Logoped'],
    },
    displayNames: { fi: 'Puheterapeutti', en: 'Speech Therapist', sv: 'Talterapeut' },
    slugs: { en: 'speech-therapist', sv: 'talterapeut' },
  },
  {
    slug: 'toimitusjohtaja',
    titleValues: {
      fi: ['Toimitusjohtaja'],
      en: ['Chief Executive Officer', 'CEO'],
      sv: ['Toimitusjohtaja', 'Verkställande direktör'],
    },
    displayNames: { fi: 'Toimitusjohtaja', en: 'Chief Executive Officer', sv: 'Verkställande direktör' },
    slugs: { en: 'chief-executive-officer', sv: 'verkstallande-direktor' },
  },
  {
    slug: 'site-reliability-engineer',
    titleValues: {
      fi: ['Site Reliability Engineer'],
      en: ['Site Reliability Engineer'],
      sv: ['Site Reliability Engineer'],
    },
    displayNames: { fi: 'Site Reliability Engineer', en: 'Site Reliability Engineer', sv: 'Site Reliability Engineer' },
  },
  {
    slug: 'laitoshuoltaja',
    titleValues: {
      fi: ['Laitoshuoltaja'],
      en: ['Laitoshuoltaja', 'Facility Cleaner', 'Hospital Cleaner'],
      sv: ['Laitoshuoltaja', 'Lokalvårdare', 'Sjukhusstädare'],
    },
    displayNames: { fi: 'Laitoshuoltaja', en: 'Facility Cleaner', sv: 'Lokalvårdare' },
    slugs: { en: 'facility-cleaner', sv: 'lokalvardare' },
  },
  {
    slug: 'asbestipurkaja',
    titleValues: {
      fi: ['Asbestipurkaja'],
      en: ['Asbestos Abatement Worker', 'Asbestipurkaja', 'Asbestos Remover', 'Asbestos Removal Worker', 'Asbestos abatement worker'],
      sv: ['Asbestipurkaja', 'Asbestsanerare', 'Asbestborttagare'],
    },
    displayNames: { fi: 'Asbestipurkaja', en: 'Asbestos Removal Worker', sv: 'Asbestsanerare' },
    slugs: { en: 'asbestos-removal-worker', sv: 'asbestsanerare' },
  },
  {
    slug: 'vuoropaallikko',
    titleValues: {
      fi: ['Vuoropäällikkö'],
      en: ['Shift Manager', 'Shift Supervisor'],
      sv: ['Skiftchef', 'Vuoropäällikkö', 'Skiftledare'],
    },
    displayNames: { fi: 'Vuoropäällikkö', en: 'Shift Manager', sv: 'Skiftchef' },
    slugs: { en: 'shift-manager', sv: 'skiftchef' },
  },
  {
    slug: 'backend-insinoori',
    titleValues: {
      fi: ['Backend-insinööri'],
      en: ['Backend Engineer'],
      sv: ['Backendingenjör', 'Backendutvecklare', 'Backend-ingenjör'],
    },
    displayNames: { fi: 'Backend-insinööri', en: 'Backend Engineer', sv: 'Backendutvecklare' },
    slugs: { en: 'backend-engineer', sv: 'backendutvecklare' },
  },
  {
    slug: 'terminaalityontekija',
    titleValues: {
      fi: ['Terminaalityöntekijä'],
      en: ['Terminal Worker'],
      sv: ['Terminalarbetare', 'Terminaalityöntekijä'],
    },
    displayNames: { fi: 'Terminaalityöntekijä', en: 'Terminal Worker', sv: 'Terminalarbetare' },
    slugs: { en: 'terminal-worker', sv: 'terminalarbetare' },
  },
  {
    slug: 'myynnin-kehitysedustaja',
    titleValues: {
      fi: ['Myynnin kehitysedustaja', 'Liiketoiminnan kehitysedustaja'],
      en: ['Business Development Representative', 'Sales Development Representative'],
      sv: ['Affärsutvecklingsrepresentant', 'Säljutvecklingsrepresentant', 'Säljutvecklare'],
    },
    displayNames: { fi: 'Myynnin kehitysedustaja', en: 'Sales Development Representative', sv: 'Säljutvecklare' },
    slugs: { en: 'sales-development-representative', sv: 'saljutvecklare' },
  },
  {
    slug: 'customer-success-manager',
    titleValues: {
      fi: ['Customer Success Manager'],
      en: ['Customer Success Manager'],
      sv: ['Customer Success Manager'],
    },
    displayNames: { fi: 'Customer Success Manager', en: 'Customer Success Manager', sv: 'Customer Success Manager' },
  },
  {
    slug: 'automyyja',
    titleValues: {
      fi: ['Automyyjä'],
      en: ['Car Salesperson', 'Automotive Salesperson', 'Automobile Salesperson'],
      sv: ['Bilförsäljare', 'Automobilförsäljare'],
    },
    displayNames: { fi: 'Automyyjä', en: 'Car Salesperson', sv: 'Bilförsäljare' },
    slugs: { en: 'car-salesperson', sv: 'bilforsaljare' },
  },
  {
    slug: 'myynti-insinoori',
    titleValues: {
      fi: ['Myynti-insinööri'],
      en: ['Sales Engineer'],
      sv: ['Säljingenjör'],
    },
    displayNames: { fi: 'Myynti-insinööri', en: 'Sales Engineer', sv: 'Säljingenjör' },
    slugs: { en: 'sales-engineer', sv: 'saljingenjor' },
  },
  {
    slug: 'vastaanottovirkailija',
    titleValues: {
      fi: ['Vastaanottovirkailija'],
      en: ['Receptionist', 'Vastaanottovirkailija', 'Front Desk Agent'],
      sv: ['Receptionist', 'Vastaanottovirkailija'],
    },
    displayNames: { fi: 'Vastaanottovirkailija', en: 'Receptionist', sv: 'Receptionist' },
    slugs: { en: 'receptionist', sv: 'receptionist' },
  },
  {
    slug: 'mekaanikko',
    titleValues: {
      fi: ['Mekaanikko'],
      en: ['Mechanic', 'Mekaanikko'],
      sv: ['Mekaniker', 'Mekaanikko'],
    },
    displayNames: { fi: 'Mekaanikko', en: 'Mechanic', sv: 'Mekaniker' },
    slugs: { en: 'mechanic', sv: 'mekaniker' },
  },
  {
    slug: 'katsastaja',
    titleValues: {
      fi: ['Katsastaja'],
      en: ['Katsastaja', 'Vehicle Inspector', 'Motor Vehicle Inspector'],
      sv: ['Katsastaja', 'Besiktningsman'],
    },
    displayNames: { fi: 'Katsastaja', en: 'Vehicle Inspector', sv: 'Besiktningsman' },
    slugs: { en: 'vehicle-inspector', sv: 'besiktningsman' },
  },
  {
    slug: 'kerroshoitaja',
    titleValues: {
      fi: ['Kerroshoitaja'],
      en: ['Kerroshoitaja', 'Housekeeping Attendant'],
      sv: ['Kerroshoitaja', 'Rumsstädare'],
    },
    displayNames: { fi: 'Kerroshoitaja', en: 'Housekeeper', sv: 'Rumsstädare' },
    slugs: { en: 'housekeeper', sv: 'rumsstadare' },
  },
  {
    slug: 'tekninen-myyja',
    titleValues: {
      fi: ['Tekninen myyjä'],
      en: ['Technical Sales Representative', 'Technical Salesperson', 'Technical Sales', 'Technical Sales Engineer'],
      sv: ['Teknisk säljare', 'Tekninen myyjä', 'Teknisk försäljare', 'Tekninen Myyjä'],
    },
    displayNames: { fi: 'Tekninen myyjä', en: 'Technical Sales Representative', sv: 'Teknisk säljare' },
    slugs: { en: 'technical-sales-representative', sv: 'teknisk-saljare' },
  },
  {
    slug: 'logistiikkatyontekija',
    titleValues: {
      fi: ['Logistiikkatyöntekijä'],
      en: ['Logistics Worker'],
      sv: ['Logistikarbetare', 'Logistiikkatyöntekijä'],
    },
    displayNames: { fi: 'Logistiikkatyöntekijä', en: 'Logistics Worker', sv: 'Logistikarbetare' },
    slugs: { en: 'logistics-worker', sv: 'logistikarbetare' },
  },
  {
    slug: 'betonielementtien-valmistaja',
    titleValues: {
      fi: ['Betonielementtien valmistaja'],
      en: ['Concrete Element Manufacturer', 'Betonielementtien valmistaja', 'Concrete element manufacturer'],
      sv: ['Betonielementtien valmistaja', 'Betongelementtillverkare', 'Betonelementtillverkare', 'Betonielementtillverkare', 'Betongelementstillverkare'],
    },
    displayNames: { fi: 'Betonielementtien valmistaja', en: 'Concrete Element Manufacturer', sv: 'Betongelementtillverkare' },
    slugs: { en: 'concrete-element-manufacturer', sv: 'betongelementtillverkare' },
  },
  {
    slug: 'engineering-manager',
    titleValues: {
      fi: ['Engineering Manager'],
      en: ['Engineering Manager'],
      sv: ['Engineering Manager'],
    },
    displayNames: { fi: 'Engineering Manager', en: 'Engineering Manager', sv: 'Engineering Manager' },
  },
  {
    slug: 'sosiaalityontekija',
    titleValues: {
      fi: ['Sosiaalityöntekijä'],
      en: ['Social Worker', 'Socialarbetare'],
      sv: ['Socialarbetare', 'Sosiaalityöntekijä'],
    },
    displayNames: { fi: 'Sosiaalityöntekijä', en: 'Social Worker', sv: 'Socialarbetare' },
    slugs: { en: 'social-worker', sv: 'socialarbetare' },
  },
  {
    slug: 'markkinointipaallikko',
    titleValues: {
      fi: ['Markkinointipäällikkö'],
      en: ['Marketing Manager', 'Head of Marketing'],
      sv: ['Marknadschef', 'Marknadsföringschef'],
    },
    displayNames: { fi: 'Markkinointipäällikkö', en: 'Marketing Manager', sv: 'Marknadschef' },
    slugs: { en: 'marketing-manager', sv: 'marknadschef' },
  },
  {
    slug: 'tuotantopaallikko',
    titleValues: {
      fi: ['Tuotantopäällikkö'],
      en: ['Production Manager'],
      sv: ['Produktionschef', 'Tuotantopäällikkö'],
    },
    displayNames: { fi: 'Tuotantopäällikkö', en: 'Production Manager', sv: 'Produktionschef' },
    slugs: { en: 'production-manager', sv: 'produktionschef' },
  },
  {
    slug: 'full-stack-kehittaja',
    titleValues: {
      fi: ['Full Stack -kehittäjä'],
      en: ['Full Stack Developer', 'Full Stack Engineer', 'Full-Stack Developer'],
      sv: ['Fullstackutvecklare', 'Fullstack-utvecklare', 'Full Stack-utvecklare', 'Full Stack -kehittäjä'],
    },
    displayNames: { fi: 'Full Stack -kehittäjä', en: 'Full Stack Developer', sv: 'Fullstackutvecklare' },
    slugs: { en: 'full-stack-developer', sv: 'fullstackutvecklare' },
  },
  {
    slug: 'raskaan-kaluston-asentaja',
    titleValues: {
      fi: ['Raskaan kaluston asentaja'],
      en: ['Heavy Vehicle Technician', 'Heavy equipment installer', 'Heavy Vehicle Mechanic', 'Heavy Equipment Technician'],
      sv: ['Montör för tung utrustning', 'Installatör av tung utrustning'],
    },
    displayNames: { fi: 'Raskaan kaluston asentaja', en: 'Heavy Vehicle Technician', sv: 'Tungfordonsmekaniker' },
    slugs: { en: 'heavy-vehicle-technician', sv: 'tungfordonsmekaniker' },
  },
  {
    slug: 'forward-deployed-engineer',
    titleValues: {
      fi: ['Forward Deployed Engineer'],
      en: ['Forward Deployed Engineer'],
      sv: ['Forward Deployed Engineer'],
    },
    displayNames: { fi: 'Forward Deployed Engineer', en: 'Forward Deployed Engineer', sv: 'Forward Deployed Engineer' },
  },
];

/**
 * True when `pathname` is a configured role hub (/jobs/role/{slug}, fi ammatti, sv yrke) for a
 * slug that exists in ROLE_HUB_CONFIG. Used by the robots layer: a `normalisedTitle` selection is
 * indexable only on its pretty role-hub path, never as a `?normalisedTitle=` query URL.
 */
export function isRoleHubPathname(pathname: string): boolean {
  const match = pathname.match(/\/(role|ammatti|yrke)\/([^/?#]+)/i);
  return Boolean(match && isRolePathMarker(match[1]) && getRoleBySlug(decodeURIComponent(match[2])));
}

/**
 * Matches `slug` against a role's canonical `slug` OR either locale override in `slugs`, so a
 * URL built under the old shared (Finnish-derived) slug on an en/sv path still resolves to the
 * right role, which resolveCanonicalRoleHubPath below needs to redirect it to the locale-correct
 * one instead of 404ing an already-indexed URL.
 */
export function getRoleBySlug(slug: string): RoleDefinition | null {
  if (!slug) return null;
  const normalized = slug.trim().toLowerCase();
  return (
    ROLE_HUB_CONFIG.find(
      (role) =>
        role.slug === normalized ||
        role.slugs?.en === normalized ||
        role.slugs?.sv === normalized ||
        Boolean(role.retiredSlugs?.includes(normalized)),
    ) ?? null
  );
}

/** Resolves the URL slug to use for `role` in `locale`: `slug` itself for `fi` (the slug's own
 * source language), an override from `slugs` for `en`/`sv` when one exists, else `slug` again. */
export function resolveRoleHubSlug(role: RoleDefinition, locale: SupportedLocale): string {
  if (locale === 'fi') return role.slug;
  return role.slugs?.[locale] ?? role.slug;
}

/**
 * Every role hub's display name for a locale, deduplicated. Used as the closed candidate set an
 * LLM feature can be asked to choose from (constrained generation) instead of generating free
 * text and filtering afterwards: every choice is then guaranteed to be a real, browsable role.
 */
export function getRoleDisplayNamesForLocale(locale: SupportedLocale): string[] {
  const seen = new Set<string>();
  for (const role of ROLE_HUB_CONFIG) {
    const name = role.displayNames[locale];
    if (name) seen.add(name);
  }
  return Array.from(seen);
}

const ROLE_JOBS_SEGMENT: Record<SupportedLocale, string> = {
  en: 'jobs',
  fi: 'tyopaikat',
  sv: 'lediga-jobb',
};

const ROLE_PATH_MARKER_BY_LOCALE: Record<SupportedLocale, string> = {
  en: 'role',
  fi: 'ammatti',
  sv: 'yrke',
};

/** The roles index (`/jobs/roles`, fi `/tyopaikat/ammatit`, sv `/lediga-jobb/yrken`). */
export function buildRolesIndexPath(locale: SupportedLocale): string {
  return `/${locale}/${ROLE_JOBS_SEGMENT[locale]}/${LOCALIZED_PATHS[locale].rolesIndex}`;
}

/** Builds the `/jobs/role/{slug}` (fi: ammatti, sv: yrke) path for a role hub in a given locale. */
export function buildRoleHubPath(slug: string, locale: SupportedLocale): string {
  return `/${locale}/${ROLE_JOBS_SEGMENT[locale]}/${ROLE_PATH_MARKER_BY_LOCALE[locale]}/${slug}`;
}

/** `buildRoleHubPath` + `resolveRoleHubSlug` in one call: the canonical URL for `role` in `locale`. */
export function buildCanonicalRoleHubPath(role: RoleDefinition, locale: SupportedLocale): string {
  return buildRoleHubPath(resolveRoleHubSlug(role, locale), locale);
}

/**
 * True when `values` is exactly the role's normalisedTitle value set for `locale`
 * (order- and case-insensitive). Used to keep the pretty /role/{slug} path and
 * omit redundant ?normalisedTitle= params when regenerating URLs from search state.
 */
export function titleValuesMatchRole(
  role: RoleDefinition,
  locale: SupportedLocale,
  values: string[],
): boolean {
  const expected = new Set(role.titleValues[locale].map((v) => v.toLowerCase()));
  const actual = new Set(values.map((v) => String(v).toLowerCase()));
  if (expected.size !== actual.size) return false;
  for (const v of actual) {
    if (!expected.has(v)) return false;
  }
  return true;
}
